/**
 * Quotes Carousel
 * 1. Create a page-level JS file (e.g. pages/newPage.js)
 * 2. In that file, grab the window.Cacoo.SiteResources object key containing the data for the quotes (thus far called QuoteData.customerQuotes)
 * 3. (For step #2, you may need to tweak the corresponding .go file)
 * 4. Instantiate a new quoteCarousel and pass in the quote data
 * 5. In the page-level JS file, create a scroll listener to auto-trigger the slideshow when the user hits a point
 **/

export default class QuotesCarousel {
  constructor(quoteData) {
    // SETTINGS - these values change carousel behavior
    // ms
    this.ANIMATION_DURATION = 100
    // ms
    this.AUTOROTATE_INTERVAL = 4000

    // Variables
    this.quoteBody = document.querySelector('.js_quote-body')
    this.quoteRole = document.querySelector('.js_quote-role')
    this.quoteSource = document.querySelector('.js_quote-source')
    this.quoteHeading = document.querySelector('.js_quote-heading')
    this.leftBtn = document.querySelector('.js_left-carousel-control')
    this.rightBtn = document.querySelector('.js_right-carousel-control')

    // A convenient, iterable variable representing a single quote
    this.quoteElements = [
      this.quoteHeading,
      this.quoteBody,
      this.quoteSource,
      this.quoteRole,
    ]

    // General initialization

    // Pull variables from Cacoo.SiteResources (To modify, see: base.go/page.go/home.go)
    this.QUOTES = quoteData

    this.QUOTE_COUNT = this.QUOTES.length
    this.autoRotateIntervalID = null

    // Track current Slide
    this.currentQuoteIndex = 0
    this.currentSlide = this.QUOTES[this.currentQuoteIndex]

    // Attach event listeners, do an initial check, and start auto rotation of quotes
    this.attachEvent()
    this.updateTextContent()
  }

  // Creates a setInterval for auto-rotating quotes.
  autoRotateQuotes() {
    this.autoRotateIntervalID = setInterval(() => {
      this.nextSlide()
    }, this.AUTOROTATE_INTERVAL)
  }

  // Attaches event listeners to each button
  attachEvent() {
    this.leftBtn.addEventListener('click', () => {
      clearInterval(this.autoRotateIntervalID)
      this.previousSlide()
    })

    this.rightBtn.addEventListener('click', () => {
      clearInterval(this.autoRotateIntervalID)
      this.nextSlide()
    })
  }

  nextSlide() {
    this.fadeOutQuotes('left')
    this.changeSlide('next')
    this.trackNewSlideData()
    setTimeout(() => {
      this.fadeInQuotes('right')
    }, this.ANIMATION_DURATION)
  }

  previousSlide() {
    this.fadeOutQuotes('right')
    this.changeSlide('previous')
    this.trackNewSlideData()
    setTimeout(() => {
      this.fadeInQuotes('left')
    }, this.ANIMATION_DURATION)
  }

  // Apply the class to the element
  applyFadeAnimation(el, type, direction, offset) {
    // This constant affects the delay BETWEEN the heading animating and the body animating, etc.
    // ..If equal to 0, the elements will animate at the same time.
    // ..Increase the value to add a "step" between each element.
    const delayBetweenAnimationSteps = offset * 1

    el.style.animationName = 'none'
    el.style.animationName = `quote-carousel-animation-${type}-${direction}-${offset}`
    el.style.animationDelay = `${delayBetweenAnimationSteps}ms`

    if (type === 'in') {
      el.style.opacity = 0
    } else {
      el.style.opacity = 1
    }
  }

  fadeOutQuotes(direction) {
    // Fade out each element
    for (let i = 0; i < this.quoteElements.length; i++) {
      const element = this.quoteElements[i]
      this.applyFadeAnimation(element, 'out', direction, i)
    }
  }

  fadeInQuotes(direction) {
    // Fade elements back in
    for (let i = 0; i < this.quoteElements.length; i++) {
      const element = this.quoteElements[i]
      this.applyFadeAnimation(element, 'in', direction, i)
    }
    this.updateTextContent()
  }

  // Iterate through all fields of the current quote (body, source, role, etc) and hide if empty
  updateTextContent() {
    // Create a map between the element and its new value
    const contentUpdateMap = [
      {
        el: this.quoteHeading,
        value: this.currentSlide.quoteHeading,
      },
      {
        el: this.quoteBody,
        value: this.currentSlide.quoteBody,
      },
      {
        el: this.quoteSource,
        value: this.currentSlide.quoteSource,
      },
      {
        el: this.quoteRole,
        value: this.currentSlide.quoteRole,
      },
    ]

    // If the new value (e.g. 'quoteRole') is empty, hide that element.
    contentUpdateMap.forEach((pair) => {
      if (pair.value === '') {
        pair.el.style.display = 'none'
      } else {
        pair.el.style.display = 'block'
        pair.el.textContent = pair.value
      }
    })
  }

  // Updates the currently tracked slide
  trackNewSlideData() {
    this.currentSlide = this.QUOTES[this.currentQuoteIndex]
  }

  // Increments or decrements the index appropriately, wrapping at upper/lower limits
  changeSlide(prevNext) {
    // Make lower-case before comparison
    prevNext = prevNext.toLowerCase()

    // Validate the type of slide move
    if (prevNext !== 'previous' && prevNext !== 'next') {
      console.error('Invalid increment type was attempted.')
      return
    }

    // Increment or decrement current slide index based on outer limits
    if (prevNext === 'next') {
      // If exceeds number of quotes, restart at the start
      if (this.currentQuoteIndex + 1 >= this.QUOTE_COUNT) {
        this.currentQuoteIndex = 0
      } else {
        this.currentQuoteIndex += 1
      }
    } else {
      if (this.currentQuoteIndex - 1 < 0) {
        this.currentQuoteIndex = this.QUOTE_COUNT - 1
      } else {
        this.currentQuoteIndex -= 1
      }
    }
  }
}
